import { Grid } from '~/engine/data/common/Grid';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { computeSliceAABB } from '~/domain/services/assembleVolume';
import { WorldBoundsMm, buildTransversalGridForBounds } from './gridGeometry';
import { MIN_SOURCE_OVERLAP } from './reformatValidation';

/**
 * The part of the patient a reformat writes out, in patient (world) mm.
 *
 * In-plane the field of view is square — one extent shared by X and Y —
 * while its centre moves freely, so the box can sit off the source's own
 * centre without the two axes drifting to different sizes. A square field
 * of view is what keeps in-plane voxels isotropic under a single
 * `matrixSize`.
 *
 * The two in-plane axes and Z carry deliberately different conventions,
 * because DICOM does:
 *
 * - `fovMm` is an **extent**, edge to edge, matching `PixelSpacing ×
 *   Columns`;
 * - `zRangeMm` is a pair of **slice centre positions**, matching
 *   `ImagePositionPatient`, which is where the scout overlay draws its
 *   lines and what the user reads as the slice range.
 *
 * Keeping Z on centres is what makes the slice count exact rather than
 * off by one: a range spanning k slice spacings holds k+1 slices.
 */
export interface ReformatRegion {
    /** Square in-plane field of view, mm (edge to edge). */
    fovMm: number;
    /** In-plane centre of the field of view in patient coordinates, mm. */
    centerXMm: number;
    centerYMm: number;
    /** First and last output slice centre in patient Z, mm. */
    zRangeMm: [number, number];
}

/**
 * Number of output slices a region holds at `sliceThicknessMm`: the slice
 * centres from the low end of the range to the high one inclusive.
 *
 * The range rarely divides exactly, so the count is rounded to the
 * nearest whole slice and the realised range — {@link regionToBoundsMm} —
 * follows the count rather than the request. Every figure the user is
 * shown comes from that realised geometry, so the difference is visible
 * instead of absorbed.
 */
export function regionSliceCount(
    region: ReformatRegion,
    sliceThicknessMm: number
): number {
    if (!(sliceThicknessMm > 0)) {
        throw new Error('regionSliceCount: slice thickness must be positive');
    }
    const [z0, z1] = region.zRangeMm;
    // An inverted range is a programming error, not a value to fix: every
    // producer of a region sorts its ends, and absorbing the inversion into
    // "one slice" would turn a caller's bug into a plausible-looking
    // single-slice output. Same stance as {@link gridSubRange}.
    if (z1 < z0) {
        throw new Error('regionSliceCount: zRangeMm must satisfy z0 <= z1');
    }
    return Math.max(1, Math.round((z1 - z0) / sliceThicknessMm) + 1);
}

/**
 * The slice range the output will actually carry, mm, as slice CENTRES.
 *
 * `regionSliceCount` rounds, so the last slice centre lands up to half a
 * thickness away from the range that was asked for. Everything the user is
 * shown, and everything that decides whether a position falls inside the
 * extension, reads this rather than the request — otherwise the panel
 * states one range and the output carries another.
 */
export function realisedZRangeMm(
    region: ReformatRegion,
    sliceThicknessMm: number
): [number, number] {
    const count = regionSliceCount(region, sliceThicknessMm);
    return [
        region.zRangeMm[0],
        region.zRangeMm[0] + (count - 1) * sliceThicknessMm,
    ];
}

/**
 * The region as a world-mm box, edge to edge on all three axes — the
 * shared box shape the rest of the codebase resolves against a grid.
 *
 * Z grows by half a slice at each end, converting the centre-to-centre
 * range into the extent the voxels actually occupy.
 */
export function regionToBoundsMm(
    region: ReformatRegion,
    sliceThicknessMm: number
): WorldBoundsMm {
    return boundsForSliceCount(
        region,
        region.zRangeMm[0],
        sliceThicknessMm,
        regionSliceCount(region, sliceThicknessMm)
    );
}

/**
 * The world-mm box holding `sliceCount` slices of `sliceThicknessMm` from
 * `firstCenterZ`, framed in plane by `region`.
 *
 * The ONE place the region's two conventions become a box, so the extent
 * the dialog measures against and the extent the grid is built on cannot
 * differ: in plane the field of view is an extent about a centre, while Z
 * is a run of slice CENTRES that grows by half a slice at each end to
 * reach the extent the voxels occupy.
 *
 * `firstCenterZ` is passed rather than read from the region because an
 * extended output starts before the region does.
 */
function boundsForSliceCount(
    region: ReformatRegion,
    firstCenterZ: number,
    sliceThicknessMm: number,
    sliceCount: number
): WorldBoundsMm {
    const halfFov = region.fovMm / 2;
    return {
        minX: region.centerXMm - halfFov,
        maxX: region.centerXMm + halfFov,
        minY: region.centerYMm - halfFov,
        maxY: region.centerYMm + halfFov,
        minZ: firstCenterZ - sliceThicknessMm / 2,
        maxZ: firstCenterZ + (sliceCount - 0.5) * sliceThicknessMm,
    };
}

/**
 * The destination grid for a reformat: transversal, isotropic in plane, exactly
 * covering the region.
 *
 * This is the ONE description of the output geometry. The dialog reads
 * its dimensions, voxel size and extent off this grid rather than
 * estimating them alongside, so what the user is shown before starting
 * and what the reformat writes cannot disagree.
 */
export function buildReformatGrid(
    region: ReformatRegion,
    matrixSize: number,
    sliceThicknessMm: number
): Grid {
    return buildGridForSliceCount(
        region,
        region.zRangeMm[0],
        matrixSize,
        sliceThicknessMm,
        regionSliceCount(region, sliceThicknessMm)
    );
}

/**
 * The grid covering `sliceCount` slices of `sliceThicknessMm` from the
 * region's first slice centre.
 *
 * Taking the count rather than deriving it is what keeps an extended
 * output exactly `base + replicas` slices long. Deriving it from an
 * already-extended Z range rounds a sum of floats, and a tie that rounds
 * DOWN yields a grid one slice short, in which the replicas overwrite an
 * acquired slice while the provenance still reports nothing excluded.
 */
function buildGridForSliceCount(
    region: ReformatRegion,
    firstCenterZ: number,
    matrixSize: number,
    sliceThicknessMm: number,
    sliceCount: number
): Grid {
    return buildTransversalGridForBounds(
        boundsForSliceCount(region, firstCenterZ, sliceThicknessMm, sliceCount),
        matrixSize,
        matrixSize,
        sliceCount
    );
}

/**
 * The region covering a whole source series — the default selection, and
 * the extent the controls open at.
 *
 * Z comes from the slices' world bounding box, not from their centres.
 * For a transversal acquisition the two coincide, because the in-plane
 * directions carry no Z: the default is then exactly the slice-centre
 * range the rest of this module assumes, and reformatting at the source's
 * own spacing reproduces its slice count. For a tilted, sagittal or
 * coronal acquisition the bounding box is WIDER than the centre range, so
 * the default over-covers by up to the tilt overhang. That direction is
 * the safe one — the default never crops data the user did not ask to
 * lose — but it does mean the default is not exactly a centre range
 * outside the transversal case.
 *
 * Derived from the slices rather than from an assembled grid so both
 * target shapes — a raw series and a loaded image — produce the same
 * region for the same acquisition.
 */
export function defaultRegionForSlices(
    slices: DicomImageSlice[]
): ReformatRegion {
    const { worldMin, worldMax } = computeSliceAABB(slices);
    return {
        fovMm: Math.max(worldMax[0] - worldMin[0], worldMax[1] - worldMin[1]),
        centerXMm: (worldMin[0] + worldMax[0]) / 2,
        centerYMm: (worldMin[1] + worldMax[1]) / 2,
        zRangeMm: [worldMin[2], worldMax[2]],
    };
}

/**
 * `region` with its in-plane centre held so the field of view keeps at
 * least {@link MIN_SOURCE_OVERLAP} of its own extent over the source on
 * each axis.
 *
 * In plane only. Z is held inside the source by its own rule, which is
 * stricter: the slice range may not leave the acquisition at all, because
 * a slice outside it has no data to resample, while in plane the padding
 * is a supported result.
 *
 * Applied wherever the centre or the field of view changes, not only where
 * the region is dragged: narrowing the field of view shrinks the reach
 * this allows, so a region parked at the limit of a wide one would
 * otherwise be left outside the source by a change to a different control.
 */
export function clampRegionCentre(
    region: ReformatRegion,
    sourceBoundsMm: WorldBoundsMm
): ReformatRegion {
    // How far past the source's edge the centre may sit: the half-extent
    // less the overlap that must remain, which is MIN_SOURCE_OVERLAP of
    // the whole field of view and so twice that of the half.
    const reach = (region.fovMm / 2) * (1 - 2 * MIN_SOURCE_OVERLAP);
    return {
        ...region,
        centerXMm: clampCentre(
            region.centerXMm,
            sourceBoundsMm.minX,
            sourceBoundsMm.maxX,
            reach
        ),
        centerYMm: clampCentre(
            region.centerYMm,
            sourceBoundsMm.minY,
            sourceBoundsMm.maxY,
            reach
        ),
    };
}

function clampCentre(
    centre: number,
    low: number,
    high: number,
    reach: number
): number {
    return Math.min(Math.max(centre, low - reach), high + reach);
}

/**
 * An opt-in extension of the output beyond the selected region along Z,
 * filled by repeating the boundary slice.
 *
 * Its purpose is photon dose calculation, not anatomy: a scan that stops a
 * few centimetres past the target under-predicts dose near its end,
 * because the scattering material that would have been there is missing.
 * Repeating the boundary slice supplies material of roughly the right
 * density in roughly the right place. It does NOT supply anatomy — it
 * extrudes whatever the boundary slice holds, shoulders and couch rails
 * included, into a prism.
 *
 * Superior and inferior are independent; the margin is shared.
 */
export interface ReformatExtension {
    /** Requested margin beyond the region, mm. */
    marginMm: number;
    superior: boolean;
    inferior: boolean;
}

/** No extension: the output stops at the selected region. */
export const NO_EXTENSION: Readonly<ReformatExtension> = Object.freeze({
    marginMm: 0,
    superior: false,
    inferior: false,
});

/**
 * Output slices added at each extended end. The requested margin is
 * rounded UP to a whole slice, so the extension is never shorter than
 * asked for; {@link extendedMarginMm} is what is actually produced.
 */
export function extensionSliceCount(
    extension: ReformatExtension,
    sliceThicknessMm: number
): number {
    if (!(sliceThicknessMm > 0)) {
        throw new Error(
            'extensionSliceCount: slice thickness must be positive'
        );
    }
    if (extension.marginMm <= 0) {
        return 0;
    }
    // Epsilon so an exact multiple does not gain a whole extra slice to
    // binary rounding: 21 / 1.4 is 15.000000000000002 in float64.
    return Math.ceil(extension.marginMm / sliceThicknessMm - 1e-9);
}

/** The margin actually produced, which the requested one rounds up to. */
export function extendedMarginMm(
    extension: ReformatExtension,
    sliceThicknessMm: number
): number {
    return extensionSliceCount(extension, sliceThicknessMm) * sliceThicknessMm;
}

/** Total replicated slices across both ends. */
export function replicatedSliceCount(
    extension: ReformatExtension,
    sliceThicknessMm: number
): number {
    const perEnd = extensionSliceCount(extension, sliceThicknessMm);
    return (
        perEnd * ((extension.superior ? 1 : 0) + (extension.inferior ? 1 : 0))
    );
}

export interface ExtendedReformatOutput {
    /** The grid the reformat writes, extension included. */
    grid: Grid;
    /** Replicated slices at the low-Z end. */
    inferiorSlices: number;
    /** Replicated slices at the high-Z end. */
    superiorSlices: number;
}

/**
 * The output grid for a region plus its extension, with the replica counts
 * that grid was built to hold.
 *
 * The slice count is COMPOSED — base plus replicas — and the grid built
 * from that number, so the three cannot disagree. They are returned
 * together for the same reason: a caller pairing this grid with counts
 * derived separately is how the disagreement got in.
 */
export function buildExtendedReformatGrid(
    region: ReformatRegion,
    matrixSize: number,
    sliceThicknessMm: number,
    extension: ReformatExtension
): ExtendedReformatOutput {
    const perEnd = extensionSliceCount(extension, sliceThicknessMm);
    const inferiorSlices = extension.inferior ? perEnd : 0;
    const superiorSlices = extension.superior ? perEnd : 0;
    const baseCount = regionSliceCount(region, sliceThicknessMm);
    // Only the START moves. The extended high end is carried by the slice
    // count, which is why it is composed rather than derived from a Z
    // range — see {@link buildGridForSliceCount}.
    const extendedStartZ =
        region.zRangeMm[0] - inferiorSlices * sliceThicknessMm;

    return {
        grid: buildGridForSliceCount(
            region,
            extendedStartZ,
            matrixSize,
            sliceThicknessMm,
            baseCount + inferiorSlices + superiorSlices
        ),
        inferiorSlices,
        superiorSlices,
    };
}

/**
 * Acquired image data the extension displaces, mm along Z.
 *
 * Non-zero only where the slice range has been narrowed at an end that is
 * being extended: the replicas then start inside the acquired range and
 * stand in for data that exists. Capped at the realised margin, because an
 * extension of 2 cm over a range cropped 10 cm short displaces 2 cm — the
 * rest is simply outside the output, which the slice range already says.
 *
 * Derived rather than passed in: the reformat holds the target and can
 * measure it, and a caller that forgot to would have produced replicas
 * over acquired data with the provenance recording nothing.
 */
export function excludedByExtensionMm(
    sourceZRangeMm: readonly [number, number],
    region: ReformatRegion,
    sliceThicknessMm: number,
    extension: ReformatExtension
): number {
    if (extensionSliceCount(extension, sliceThicknessMm) === 0) {
        return 0;
    }
    const marginMm = extendedMarginMm(extension, sliceThicknessMm);
    const [sourceLow, sourceHigh] = sourceZRangeMm;
    // The REALISED ends, because that is where the replicas attach: the
    // slice count rounds, so the last output centre sits up to half a
    // thickness from the range that was asked for, and measuring the
    // displaced data from the request put this figure out by that much.
    const [low, high] = realisedZRangeMm(region, sliceThicknessMm);
    const below = extension.inferior
        ? Math.min(marginMm, Math.max(0, low - sourceLow))
        : 0;
    const above = extension.superior
        ? Math.min(marginMm, Math.max(0, sourceHigh - high))
        : 0;
    return below + above;
}
